import { Box, Card, CardContent, Typography } from "@mui/material";
import { useState } from "react";
import type { DocAccessSubject, DocAccessSubjectType } from "@xop/dashboard-shared";

import { PageHeader } from "../../../components/PageHeader.js";
import { AccessEditor } from "./AccessEditor.js";
import { SubjectRail } from "./SubjectRail.js";

/**
 * Documentation ▸ Access — who sees which articles.
 *
 * The problem this replaces: the only reachable control was a per-folder
 * "show to customers" toggle, which is one decision applied to every
 * account at once. A customer who bought Mass Notification saw the Webcast
 * manuals, and the only way to stop that was to hide the folder from
 * everybody.
 *
 * The shape here follows the question staff actually ask, which is about an
 * ACCOUNT and not about a document: pick a customer or a reseller on the
 * left, and the right side becomes the library as that account sees it,
 * with the reason on every row. Editing is the same gesture as reading —
 * the control sits beside the answer it changes, and the answer updates as
 * you go rather than after a save. The right side is `AccessEditor`, which
 * the CRM reseller profile mounts too (its Documentation tab), so one
 * reseller can be curated from either place.
 *
 * Two things are deliberately NOT here. There is no per-document "who sees
 * this" editor (that belongs on the document, and the document editor's
 * scope/product panel is where a library-wide decision is made). And there
 * is no bulk "apply to all accounts" — an override that applies to everyone
 * is not an override, it is a library decision, and doing it here would
 * quietly recreate the folder toggle this page exists to demote.
 */
export function AccessPage(): JSX.Element {
  const [subjectType, setSubjectType] = useState<DocAccessSubjectType>("organization");
  const [selected, setSelected] = useState<DocAccessSubject | null>(null);

  const subjectId = selected?.id ?? null;

  return (
    <Box>
      <PageHeader
        title="Documentation access"
        subtitle="Decide which articles each customer and each reseller can read. Products decide by default; overrides here are the exceptions. A reseller's own profile carries the same editor on its Documentation tab."
      />

      <Box sx={{ display: "grid", gap: 3, gridTemplateColumns: { lg: "320px 1fr" }, mt: 2 }}>
        <Box component="aside" sx={{ minWidth: 0 }}>
          <SubjectRail
            type={subjectType}
            onTypeChange={(next) => {
              setSubjectType(next);
              setSelected(null);
            }}
            selectedId={subjectId}
            onSelect={setSelected}
          />
        </Box>

        <Box sx={{ minWidth: 0 }}>
          {subjectId == null ? (
            <Card variant="outlined">
              <CardContent>
                <Typography variant="body2" color="text.secondary">
                  Pick a customer or a reseller to see the library as they see it.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            // Keyed on the subject: switching accounts with unsaved edits
            // would otherwise silently apply them to the wrong account or
            // silently drop them. Remounting drops them — the safe half —
            // and the editor's pending-count bar is what stops it being a
            // surprise.
            <AccessEditor
              key={`${subjectType}:${subjectId}`}
              subjectType={subjectType}
              subjectId={subjectId}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}
